import { useQuery } from '@apollo/client';
import WarningIcon from '@atlaskit/icon/glyph/warning';
import { R300 } from '@atlaskit/theme/colors';
import {
  CopyableText,
  DynamicResultTable,
  FormattedDate,
  getCASSIBaseURL,
  NoDataContentInTable,
  useFeatureToggle,
} from '@css-xp/base';
import {
  CloudProductDetailView,
  EntitlementStatus,
  getBilledForColumnData,
  NextBillExpiryDate,
  LastBilledDate,
} from '@css-xp/base/entitlement-utils';
import {
  BillingFrequency,
  FetchLinkedEntitlementsQuery,
  FetchLinkedEntitlementsQueryVariables,
  LinkedDestinationEntitlement,
  LinkedEntitlementTypes,
  LinkedProductTypes,
  Maybe,
} from '@css-xp/graphtypes/src/generated-types-new';
import { CLOUD_SITE_PATH, CLOUD_SITE_PATH_2, SourceSystem } from '@atlassian/cssxp-typescommon';
import { NO_DETAILS_TO_SHOW } from '@atlassian/cssxp-utils-entitlement';
import { ENABLE_CLOUD_SITE_AC_REDIRECTION } from '@atlassian/cssxp-utils-generic';
import capitalize from 'lodash/capitalize';
import React from 'react';
import { Link } from 'react-router-dom';
import { fetchLinkedEntitlements } from '../../queries/fetch-linked-entitlements';

export const emptyStateOrderProp = {
  header: '',
  description: NO_DETAILS_TO_SHOW,
  imageUrl: '',
  testId: 'no-linked-entitlements-state',
};

export interface LinkedEntitlementsProps {
  id: string | undefined;
  currentEntitlementType?: LinkedEntitlementTypes;
  destinationEntitlementType: Array<LinkedEntitlementTypes>;
  productType: Maybe<LinkedProductTypes> | undefined;
  findProducts?: boolean;
  findApps?: boolean;
  entitlementId: string;
  tableHeading?: string;
  baseProduct?: string;
}

const LinkedEntitlementsTab = ({
  id,
  currentEntitlementType,
  destinationEntitlementType,
  productType,
  findProducts,
  findApps,
  entitlementId,
  baseProduct = '',
  tableHeading = 'Apps',
}: LinkedEntitlementsProps) => {
  const [isEnabled] = useFeatureToggle();
  const cassiBaseURL = getCASSIBaseURL();
  const { data, loading, error } = useQuery<FetchLinkedEntitlementsQuery, FetchLinkedEntitlementsQueryVariables>(
    fetchLinkedEntitlements,
    {
      variables: {
        id: entitlementId,
        searchLinkedEntitlementParams: {
          entitlementId,
          containerId: id ?? null,
          currentEntitlementType,
          destinationEntitlementType,
          productType,
          findProducts,
          findApps,
        },
      },
      displayName: 'fetchAppEntitlements',
      notifyOnNetworkStatusChange: true,
    }
  );
  const linkedEntitlements: Array<LinkedDestinationEntitlement> = data?.entitlementDetails
    .linkedEntitlements as Array<LinkedDestinationEntitlement>;

  const getErrorState = React.useCallback((error: string | undefined) => {
    const renderImage = () => <WarningIcon label="Error" size="xlarge" primaryColor={R300} />;
    return {
      header: '',
      description: (
        <span className="error-view">Oh no! Something went wrong, please try again. {error && error.toString()}</span>
      ),
      renderImage,
    };
  }, []);

  const getRows = () => {
    return (linkedEntitlements ?? [])
      .filter((entitlement) => entitlement.entitlementId !== entitlementId)
      .filter((entitlement) => (baseProduct ? entitlement.baseProduct === baseProduct : true))
      .map((item, index) => {
        const cloudSiteHostname = item?.cloudSiteHostname ?? 'N/A';
        const sitePath = item?.migrated?.toUpperCase() === SourceSystem.CCP ? CLOUD_SITE_PATH_2 : CLOUD_SITE_PATH;
        return {
          key: index.toString(),
          cells: [
            {
              content: (
                <CloudProductDetailView
                  logoId={item?.logoId as string}
                  productId={item?.productId as string}
                  entitlementNumber={item.entitlementNumber ?? ''}
                  provisioningStatus={item?.provisioningStatus}
                  edition={item?.edition}
                  productName={item?.productName}
                  cloudSiteId={item?.cloudSiteId}
                  trial={item?.trial ?? false}
                  offeringPricingType={item?.offering?.pricingType as string}
                  isApp={item?.isApp as boolean}
                  isSandbox={item?.isSandbox as boolean}
                />
              ),
            },
            {
              content: (
                <div className="line-clamp site-name-container" title={cloudSiteHostname}>
                  {cloudSiteHostname ? (
                    isEnabled(ENABLE_CLOUD_SITE_AC_REDIRECTION) ? (
                      <CopyableText duration={0}>
                        <Link to={`${sitePath}/${item.cloudSiteId}`}>{cloudSiteHostname}</Link>
                      </CopyableText>
                    ) : (
                      <CopyableText duration={0}>
                        <Link
                          to={{ pathname: `${cassiBaseURL}${CLOUD_SITE_PATH}/${item.cloudSiteId}` }}
                          target="_blank"
                        >
                          {cloudSiteHostname}
                        </Link>
                      </CopyableText>
                    )
                  ) : (
                    <span data-testid="empty-cloud-site" className="data-unavailable">
                      N/A
                    </span>
                  )}
                </div>
              ),
            },
            {
              content: (
                <div>
                  <div>{capitalize(getBilledForColumnData(item?.productId, item?.edition))}</div>
                  <div>{item?.activeUsers ?? '0'}</div>
                </div>
              ),
            },
            {
              content: item.createdDate ? <FormattedDate date={item.createdDate} /> : <NoDataContentInTable />,
            },
            {
              content: item.billingStatus ? (
                <EntitlementStatus
                  billingStatus={item.billingStatus}
                  provisioningStatus={item?.provisioningStatus}
                  scheduledChanges={item.scheduledChanges}
                />
              ) : (
                <NoDataContentInTable />
              ),
            },
            {
              content: (
                <>
                  <p>{item?.billingFrequency ? capitalize(item.billingFrequency) : <NoDataContentInTable />}</p>
                  {item?.billingFrequency === BillingFrequency.ANNUAL && item?.userTier && (
                    <p data-testid="linked-ent-user-tier">User tier - {item?.userTier}</p>
                  )}
                </>
              ),
            },
            {
              content: <LastBilledDate item={item} />,
            },
            {
              content: <NextBillExpiryDate item={item} />,
            },
          ],
        };
      });
  };

  const getHead = () => {
    const cells = [
      {
        content: 'Name',
        shouldTruncate: true,
        width: 25,
      },
      {
        content: 'Site name',
        width: 25,
      },
      {
        content: 'Usage',
        width: 10,
      },
      {
        content: 'Added',
        width: 15,
      },
      {
        content: 'Status',
        width: 10,
      },
      {
        content: 'Billing cycle',
        width: 10,
      },
      {
        content: 'Last billed',
        width: 10,
      },
      {
        content: 'Next bill / Expiry',
        width: 10,
      },
    ];
    return {
      cells,
    };
  };

  return (
    <div className="statuspage-tab" data-testid="statuspage-add-ons">
      <DynamicResultTable
        testId="linked-entitlements"
        headingText={tableHeading}
        head={getHead()}
        rows={getRows()}
        emptyStateProps={emptyStateOrderProp}
        isLoading={loading}
        errorStateProps={getErrorState(error?.message.toString())}
        error={error?.message}
      />
    </div>
  );
};

export default LinkedEntitlementsTab;
